(() => {
    // ===== Selectors =====
    const SEL = {
        openBtn: '.btn--popup--js',
        wrapper: '.wrapper--items--popup',
        overlay: '.absolute--bg--popup',
        close:   '.js--close-popup',
        item:    '.as-seen-in-your-home-video',
        active:  '.as-seen-in-your-home-video.active',
        toggle:  '.as-seen-toggle',
        bar:     '.as-seen-progress span',
        box:     '.relative--bg--popup',
        nav:     '.as-seen-nav',
        replay:  '.as-seen-replay',
        counter: '.as-seen-counter__current',
    };

    const $root = document.documentElement;

    // Failures used to be swallowed silently. Log them with a fixed prefix so
    // Safari Web Inspector on a real iPhone shows why a video did not play.
    const log = (...args) => console.warn('[as-seen]', ...args);

    // Custom storefront events (as_seen_video_open / _play / _complete / _next /
    // _previous). A Customer Events pixel can forward them to GA4; without one
    // they are inert. _next/_previous describe the video moved TO, plus how
    // (trigger: arrow, swipe, key or end_button).
    const track = (name, $item, extra = {}) => {
        try {
            window.Shopify?.analytics?.publish?.(name, {
                product_id: getDialog()?.dataset.productId || null,
                video_id: $item?.dataset.id || null,
                position: Number($item?.dataset.position) || null,
                ...extra,
            });
        } catch (err) {
            log('track failed:', name, err.message);
        }
    };

    // ===== Video utils =====
    const getVideo = ($node) => $node?.querySelector?.('video') || null;

    const stopVideo = ($node, reset = true) => {
        const v = getVideo($node);
        if (!v) return;
        v.pause?.();
        if (reset) { try { v.currentTime = 0; } catch(_) {} }
    };

    // A variant-switch morph can rewrite <source src> on a <video> the browser
    // has already loaded; only then does it need load() to re-read its sources.
    // Calling load() on every open threw the buffer away and restarted the fetch.
    const needsReload = (v) => {
        if (v.error) return true;
        if (!v.currentSrc) return false;
        const sources = [...v.querySelectorAll('source')].map((s) => s.src);
        return !sources.includes(v.currentSrc);
    };

    // The videos are uploaded without sound, so they always play muted and
    // there is no volume control. Tap-to-pause/play replaces the native controls.
    const setPaused = ($item, paused) => {
        $item.classList.toggle('is-paused', paused);
        $item.querySelector(SEL.toggle)?.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    };

    const playVideo = (v) => {
        v.muted = true;
        if (needsReload(v)) {
            log('reloading sources', v.currentSrc, v.error ? `error code ${v.error.code}` : '');
            v.load();
        }
        const p = v.play?.();
        if (!p || !p.catch) return;
        p.catch((err) => {
            log('play() rejected:', err.name, err.message);
            // AbortError = closed or switched before it started; nothing to show.
            const $item = v.closest(SEL.item);
            if (err.name !== 'AbortError' && $item?.classList.contains('active')) setPaused($item, true);
        });
    };

    // ===== Covers =====
    // Each video's full-size cover (data-poster, ~1080px wide) is not in the page
    // HTML, so it never competes with the page load. Once the page has loaded
    // and the browser is idle, fetch it quietly; when it arrives it becomes both
    // the poster and the video's background. The background is what shows before
    // the first frame even where the browser hides the poster as playback starts,
    // so it has to be the sharp cover, not the 120px thumbnail.
    const loadCover = (v, priority = 'low') => {
        const url = v.dataset.poster;
        if (!url || v.dataset.coverReady || v.dataset.coverLoading) return;
        v.dataset.coverLoading = '1';
        const img = new Image();
        img.fetchPriority = priority;
        img.onload = () => {
            v.dataset.coverReady = '1';
            if (!v.getAttribute('poster')) v.poster = url;
            v.style.backgroundImage = `url("${url}")`;
        };
        img.onerror = () => { delete v.dataset.coverLoading; log('cover failed:', url); };
        img.src = url;
    };

    const preloadPosters = () => {
        document.querySelectorAll(`${SEL.item} video[data-poster]`).forEach((v) => loadCover(v));
    };

    // Safari has no requestIdleCallback; a short delay after load is enough there.
    const whenIdle = (fn) => (window.requestIdleCallback
        ? requestIdleCallback(fn, { timeout: 3000 })
        : setTimeout(fn, 300));

    if (document.readyState === 'complete') whenIdle(preloadPosters);
    else window.addEventListener('load', () => whenIdle(preloadPosters), { once: true });

    // ===== Popup control =====
    // Always query fresh so morph-replaced DOM nodes are current.
    const getDialog = () => document.querySelector(SEL.wrapper);

    const setEnded = ($dialog, ended) => $dialog?.querySelector(SEL.box)?.classList.toggle('is-ended', ended);

    const resetPopupState = () => {
        document.querySelectorAll(SEL.item).forEach(($item) => delete $item.dataset.asSeenPlayed);
        document.querySelectorAll(SEL.active).forEach(($item) => {
            $item.classList.remove('active');
            stopVideo($item);
            setPaused($item, false);
        });
        setEnded(getDialog(), false);
        getDialog()?.classList.remove('active');
        $root.classList.remove('popup__active');
    };

    // Make $target the visible video: stop the others, update the counter and
    // arrows (no wrap: no previous on the first, no next on the last), play.
    const showItem = ($dialog, $target) => {
        $dialog.querySelectorAll(SEL.active).forEach(($item) => {
            $item.classList.remove('active');
            stopVideo($item);
            setPaused($item, false);
        });
        setEnded($dialog, false);
        $target.classList.add('active');

        const $items = [...$dialog.querySelectorAll(SEL.item)];
        const i = $items.indexOf($target);
        const $counter = $dialog.querySelector(SEL.counter);
        if ($counter) $counter.textContent = i + 1;
        $dialog.querySelectorAll(SEL.nav).forEach(($n) => {
            $n.hidden = $n.dataset.dir === 'prev' ? i <= 0 : i >= $items.length - 1;
        });

        const v = getVideo($target);
        if (v) {
            // Normally the sharp cover is already the background (preloaded after
            // page load). If the tap came first, show the already-loaded small
            // thumbnail as an instant stand-in (never a black box) and fetch the
            // cover now at high priority; it replaces the stand-in on arrival.
            if (!v.dataset.coverReady) {
                const thumb = document.querySelector(`${SEL.openBtn}[data-id="${CSS.escape($target.dataset.id)}"] img`);
                if (thumb?.currentSrc && !v.style.backgroundImage) v.style.backgroundImage = `url("${thumb.currentSrc}")`;
                loadCover(v, 'high');
            }
            playVideo(v);
        }
        else log('no <video> in popup item', $target.dataset.id);
    };

    // The focused arrow or end button can disappear (first/last video, end
    // panel hidden); keep keyboard focus inside the popup.
    const keepFocus = ($dialog, $item, hadFocus) => {
        const $f = document.activeElement;
        if (hadFocus && (!$f || $f === document.body || !$f.getClientRects().length)) {
            ($dialog.querySelector(`${SEL.nav}:not([hidden]):not(.as-seen-end__btn)`)
                || $item.querySelector(SEL.toggle))?.focus({ preventScroll: true });
        }
    };

    const focusInside = ($dialog) => $dialog.contains(document.activeElement) && document.activeElement !== $dialog;

    // dir: 'next' | 'prev'. Returns quietly at either end.
    const step = (dir, trigger) => {
        const $dialog = getDialog();
        const $current = $dialog?.querySelector(SEL.active);
        if (!$dialog?.hasAttribute('open') || !$current) return;
        const $items = [...$dialog.querySelectorAll(SEL.item)];
        const $target = $items[$items.indexOf($current) + (dir === 'next' ? 1 : -1)];
        if (!$target) return;

        const hadFocus = focusInside($dialog);
        showItem($dialog, $target);
        track(dir === 'next' ? 'as_seen_video_next' : 'as_seen_video_previous', $target, { trigger });
        keepFocus($dialog, $target, hadFocus);
    };

    const openPopup = (id) => {
        const $dialog = getDialog();
        const $target = $dialog?.querySelector(`${SEL.item}[data-id="${CSS.escape(id)}"]`);
        if (!$dialog || !$target) {
            log('popup or video not found for id', id);
            return;
        }

        // showModal() puts the popup in the top layer, above every stacking
        // context on the page. Fall back to the open attribute if it is missing
        // or throws (e.g. the dialog was left open non-modally by a morph).
        if (!$dialog.open) {
            try {
                if (typeof $dialog.showModal !== 'function') throw new Error('showModal unsupported');
                $dialog.showModal();
            } catch (err) {
                log('showModal failed, using open attribute:', err.name || '', err.message);
                $dialog.setAttribute('open', '');
            }
        }
        $dialog.classList.add('active');
        $root.classList.add('popup__active');

        track('as_seen_video_open', $target);
        showItem($dialog, $target);
    };

    const closePopup = () => {
        const $dialog = getDialog();
        if ($dialog?.open && typeof $dialog.close === 'function') {
            $dialog.close(); // fires 'close', which resets state below
        } else {
            $dialog?.removeAttribute('open');
            resetPopupState();
        }
    };

    // ===== Events (delegated — survive morph) =====
    document.addEventListener('click', (e) => {
        const $toggle = e.target.closest(SEL.toggle);
        if ($toggle) {
            e.preventDefault();
            const v = getVideo($toggle.closest(SEL.item));
            if (!v) return;
            if (v.paused || v.ended) playVideo(v);
            else v.pause();
            return;
        }

        const $nav = e.target.closest(SEL.nav);
        if ($nav) {
            e.preventDefault();
            step($nav.dataset.dir, $nav.classList.contains('as-seen-end__btn') ? 'end_button' : 'arrow');
            return;
        }

        if (e.target.closest(SEL.replay)) {
            e.preventDefault();
            const $dialog = getDialog();
            const $item = $dialog?.querySelector(SEL.active);
            const v = getVideo($item);
            if (!v) return;
            const hadFocus = focusInside($dialog);
            setEnded($dialog, false);
            playVideo(v);
            keepFocus($dialog, $item, hadFocus);
            return;
        }

        const $btn = e.target.closest(SEL.openBtn);
        if ($btn) {
            e.preventDefault();
            const id = $btn.dataset.id;
            if (id) openPopup(id);
            else log('thumbnail has no data-id');
            return;
        }

        if (e.target.closest(SEL.overlay) || e.target.closest(SEL.close)) {
            e.preventDefault();
            closePopup();
        }
    });

    // 'close' (Escape, close()) and media 'error' do not bubble; listen in capture.
    document.addEventListener('close', (e) => {
        if (e.target.matches?.(SEL.wrapper)) resetPopupState();
    }, true);

    // Pause/play icon and progress bar follow the video's own events.
    document.addEventListener('play', (e) => {
        const $item = e.target.closest?.(SEL.item);
        if (!$item) return;
        setPaused($item, false);
        if ($item.classList.contains('active')) setEnded(getDialog(), false);
    }, true);

    document.addEventListener('pause', (e) => {
        const $item = e.target.closest?.(SEL.item);
        // Closing the popup pauses too; only show the play icon while it is open.
        if ($item?.classList.contains('active')) setPaused($item, true);
    }, true);

    document.addEventListener('timeupdate', (e) => {
        const v = e.target;
        const $bar = v.closest?.(SEL.item)?.querySelector(SEL.bar);
        if ($bar && v.duration) $bar.style.transform = `scaleX(${Math.min(v.currentTime / v.duration, 1)})`;
    }, true);

    // One 'play' per open (flag cleared on close), and 'complete' when a video ends.
    document.addEventListener('playing', (e) => {
        const $item = e.target.closest?.(SEL.item);
        if (!$item || $item.dataset.asSeenPlayed) return;
        $item.dataset.asSeenPlayed = '1';
        track('as_seen_video_play', $item);
    }, true);

    // No auto-advance: an ended video shows Replay (and Next video, unless last).
    document.addEventListener('ended', (e) => {
        const $item = e.target.closest?.(SEL.item);
        if (!$item) return;
        track('as_seen_video_complete', $item);
        if ($item.classList.contains('active')) setEnded(getDialog(), true);
    }, true);

    // Swipe left/right on the popup to change video. Mostly-horizontal and at
    // least 40px; a vertical drag or a tap is left alone.
    let touchStart = null;
    document.addEventListener('touchstart', (e) => {
        touchStart = e.touches.length === 1 && e.target.closest?.(SEL.wrapper)
            ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
            : null;
    }, { passive: true });

    document.addEventListener('touchend', (e) => {
        if (!touchStart) return;
        const dx = e.changedTouches[0].clientX - touchStart.x;
        const dy = e.changedTouches[0].clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 'next' : 'prev', 'swipe');
    }, { passive: true });

    document.addEventListener('error', (e) => {
        const $t = e.target;
        if (!$t.closest?.(SEL.wrapper)) return;
        if ($t.tagName === 'SOURCE') log('source failed:', $t.type, $t.src);
        else if ($t.tagName === 'VIDEO') log('video error:', $t.error?.code, $t.error?.message, $t.currentSrc);
    }, true);

    // Left/right arrow keys change video; Escape closes (fallback path only:
    // a modal dialog closes itself on Escape).
    document.addEventListener('keydown', (e) => {
        const $dialog = getDialog();
        if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && $dialog?.hasAttribute('open')) {
            e.preventDefault();
            step(e.key === 'ArrowRight' ? 'next' : 'prev', 'key');
            return;
        }
        let isModal = false;
        try { isModal = !!$dialog?.matches(':modal'); } catch (_) {}
        if (e.key === 'Escape' && $dialog?.hasAttribute('open') && !isModal) {
            closePopup();
        }
    });

    // ===== Re-init after morph =====
    // Morph replaces main content without re-executing <script defer> tags.
    // Delegation on document needs no re-binding; just clear stale state if a
    // morph removed or closed the popup while it was open.
    const mainEl = document.querySelector('main');
    if (!mainEl) return;

    let debounce;
    new MutationObserver(() => {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
            if (!getDialog()?.open && $root.classList.contains('popup__active')) resetPopupState();
        }, 200);
    }).observe(mainEl, { childList: true, subtree: true });
})();
